// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type GroupRow, useGroupsQuery } from "@/api/hooks/use-groups";
import type { components } from "@/api/schema";
import { DataTable } from "@/components/data-table";
import { GroupPlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { DateValue } from "@/components/shared/datetime-value";
import { RowId } from "@/components/shared/row-id";
import { TimezoneFooterNote } from "@/components/shared/timezone-footer-note";
import { Button } from "@/components/ui/button";
import { CreateGroupDialog } from "@/features/iam/create-group-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/iam/groups/index";

type GroupsSortBy = components["schemas"]["GroupsSortByEnum"];

const FILTER_DEFAULTS = {
  search: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "name",
  sortDesc: false,
};

// Table column id → backend sort key. Only these columns are sortable.
const SORT_BY_MAP: Record<string, GroupsSortBy> = {
  groupId: "groupId",
  name: "name",
  noOfUsers: "noOfUsers",
  createdAt: "createdAt",
};

export function GroupsList() {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamGroupsEdit);
  const navigate = useNavigate();
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "name",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/iam/groups",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [search, pagination.pageIndex, pagination.pageSize, sorting, navigate],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = SORT_BY_MAP[sorting[0]?.id ?? "name"] ?? "name";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useGroupsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
  });

  const columns = useMemo<ColumnDef<GroupRow, unknown>[]>(
    () => [
      {
        accessorKey: "groupId",
        header: t("groups.list.columns.id"),
        size: 80,
        enableSorting: true,
        enableHiding: false,
        meta: { columnLabel: t("groups.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.groupId} />,
      },
      {
        accessorKey: "name",
        header: t("groups.list.columns.name"),
        enableSorting: true,
        meta: { columnLabel: t("groups.list.columns.name") },
        cell: ({ row, table }) => {
          // Show the description as a muted sub-line only when its dedicated column is hidden.
          const descriptionColumnVisible = table.getColumn("description")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              <Link
                to="/iam/groups/$groupName"
                params={{ groupName: row.original.name }}
                className="font-medium text-primary hover:underline"
              >
                {row.original.name}
              </Link>
              {row.original.description && !descriptionColumnVisible && (
                <span className="font-normal text-muted-foreground text-xs">{row.original.description}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "description",
        header: t("groups.list.columns.description"),
        enableSorting: false,
        meta: { columnLabel: t("groups.list.columns.description"), defaultHidden: true },
        cell: ({ row }) => row.original.description || <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: "noOfUsers",
        header: t("groups.list.columns.noOfUsers"),
        size: 100,
        enableSorting: true,
        meta: { columnLabel: t("groups.list.columns.noOfUsers") },
        cell: ({ row }) => <span className="tabular-nums">{row.original.noOfUsers}</span>,
      },
      {
        accessorKey: "createdAt",
        header: t("groups.list.columns.createdAt"),
        enableSorting: true,
        meta: { columnLabel: t("groups.list.columns.createdAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("groups.title")} />
      <div className="p-6">
        <DataTable
          storageKey="groups-list"
          columns={columns}
          data={data?.groups ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={500}
          onRefresh={refetch}
          isRefreshing={isFetching}
          searchPlaceholder={t("groups.list.searchPlaceholder")}
          toolbar={
            canEdit ? (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <GroupPlusIcon className="h-4 w-4" />
                {t("groups.list.addGroupButton")}
              </Button>
            ) : null
          }
          footerNote={<TimezoneFooterNote />}
        />
      </div>

      <CreateGroupDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  );
}
